import React from "react"
import { Box, Button, Flex, Text } from "@chakra-ui/react"
import colors from "@constants/colors"
import animations from "@constants/animations"
import Link from "@components/Link"
import Layout from "@components/Layout/hoc/withCmsData"
import Helmet from "@components/Helmet"
import { Filter, Grid, List } from "@components/universal/icons"
import Filters from "./components/Filters"
import FilterSlideout from "./components/FilterSlideout"
import ProductGrid from "./components/ProductGrid"
import ProductList from "./components/ProductList"
import SortDropdown from "./components/SortDropdown"
import Spacer from "@components/universal/Spacer"
import useScrollHandler from "@hooks/useScrollHandler"
import { sortByIndex } from "@utils/index"
import { bp, MobileOnly, LaptopUpOnly } from "@utils/MediaQueries"
import {
  ProductView,
  SortMethods,
  productSliderFilters,
  filterPropMap,
} from "./common"
import {
  getBreadcrumbs,
  getFlattenedProducts,
  getFilterValues,
  formatProduct,
  formatDisplayedCategory,
} from "./utils"
import { usePreviewSubscription } from "@utils/sanity"
import _ from "lodash"

const productGridRef = React.createRef<HTMLDivElement>()
const productListMobileRef = React.createRef<HTMLDivElement>()
const productListDesktopRef = React.createRef<HTMLDivElement>()

const Collections: any = ({
  preview,
  query,
  queryVariables,
  pageData: staticData,
  path,
  categoryMap,
  categoryConfig,
  useNewReleases,
}) => {
  // const { data: liveData } = usePreviewSubscription(query, {
  //   params: queryVariables,
  //   initialData: staticData,
  //   enabled: true,
  // })

  const pageData = staticData

  const categoriesForPath = (path && categoryMap && categoryMap[path[0]]) || []
  const categoriesData = pageData?.categories || []
  const categories = _.sortBy(
    categoriesData.filter(({ _id }) => categoriesForPath.indexOf(_id) !== -1),
    ({ slug }) => path.indexOf(slug)
  )

  const featuredSlugs = React.useMemo(() => {
    return (
      _.uniq(
        _.flatten(
          _.reverse(
            categories
              .filter(({ featured }) => featured && featured.length)
              .map(({ featured }) => featured.map(({ current }) => current))
          )
        )
      ) || []
    )
  }, [categories])

  const placeholderImage = _.get(pageData, "placeholderImage[0].url")
  const allProducts: Array<any> = React.useMemo(() => {
    const products = !useNewReleases
      ? pageData?.products || []
      : pageData?.newReleases.length > 0
      ? pageData?.newReleases || []
      : pageData?.mostRecentProducts || []
    return products.map((product) => formatProduct(product, placeholderImage))
  }, [pageData, path])

  const flattenedProducts: Array<any> = React.useMemo(() => {
    const products = pageData?.products || []
    return getFlattenedProducts(products)
  }, [path, allProducts])

  const products = React.useMemo(() => {
    return {
      [SortMethods.ByFeatured]: sortByIndex(
        _.reverse(
          _.sortBy(allProducts, ({ releaseDate }) => new Date(releaseDate))
        ),
        "slug",
        featuredSlugs
      ),
      [SortMethods.ByNewest]: _.reverse(
        _.sortBy(allProducts, ({ releaseDate }) => new Date(releaseDate))
      ),
      [SortMethods.ByPriceAsc]: _.sortBy(allProducts, ({ price }) =>
        parseFloat(price)
      ),
      [SortMethods.ByPriceDesc]: _.reverse(
        _.sortBy(allProducts, ({ price }) => parseFloat(price))
      ),
    }
  }, [allProducts])

  const [sortMethod, setSortMethod] = React.useState<SortMethods>(
    SortMethods.ByFeatured
  )

  const onSort = (sortMethod) => {
    setSortMethod(sortMethod)
  }

  const [renderedProductCount, setRenderedProductCount] = React.useState(36)

  const onScrollHandler = () => {
    if (
      typeof window == "undefined" ||
      (!productGridRef && !productListMobileRef && !productListDesktopRef) ||
      (!productGridRef.current &&
        !productListMobileRef.current &&
        !productListDesktopRef.current) ||
      allProducts.length <= renderedProductCount
    ) {
      return
    }

    const productGridNodes = productGridRef?.current?.childNodes as any
    const productListMobileNodes = productListMobileRef?.current
      ?.childNodes as any
    const productListDesktopNodes = productListDesktopRef?.current
      ?.childNodes as any

    const positionReference: any = productGridNodes
      ? productGridNodes[productGridNodes?.length - 9] ||
        productGridNodes[productGridNodes?.length - 6] ||
        productGridNodes[productGridNodes?.length - 3]
      : productListMobileNodes[productListMobileNodes?.length - 6] ||
        productListMobileNodes[productListMobileNodes?.length - 4] ||
        productListMobileNodes[productListMobileNodes?.length - 2] ||
        productListDesktopNodes[productListDesktopNodes?.length - 6] ||
        productListDesktopNodes[productListDesktopNodes?.length - 3]

    if (positionReference?.getBoundingClientRect().top < window.innerHeight!) {
      setRenderedProductCount(renderedProductCount + 36)
    }
  }

  useScrollHandler({
    onScroll: onScrollHandler,
    runForProps: [pageData, renderedProductCount],
  })

  const [renderedProducts, setRenderedProducts] = React.useState<any>(
    _.take(products[SortMethods.ByFeatured], renderedProductCount)
  )

  const filters = React.useMemo(() => {
    return getFilterValues(flattenedProducts)
  }, [path, flattenedProducts])

  const [availableFilters, setAvailableFilters] = React.useState<any>(filters)

  React.useEffect(() => {
    setAvailableFilters(filters)
  }, [filters])

  const [activeFilters, setActiveFilters] = React.useState<{
    filters: {
      [filterName: string]: {
        values?: Array<string>
        min?: number
        max?: number
        pristine?: boolean
      }
    }
  }>({
    filters: {},
  })

  const onFilter = (filters) => {
    setActiveFilters({ filters })
  }

  const [lastClear, setLastClear] = React.useState(Date.now())

  const onClearFilter = () => {
    setActiveFilters({
      filters: {},
    })
    setLastClear(Date.now())
  }

  React.useEffect(() => {
    onClearFilter()
  }, [pageData, path])

  const filterPanelRef = React.createRef<HTMLDivElement>()
  const [offset, setOffset] = React.useState(0)
  React.useEffect(() => {
    if (filterPanelRef && filterPanelRef.current) {
      setOffset(filterPanelRef.current?.offsetTop)
    }
  }, [filterPanelRef])

  const [filteredProductCount, setFilteredProductCount] =
    React.useState<number>(allProducts.length)

  React.useEffect(() => {
    setFilteredProductCount(allProducts.length)
  }, [allProducts])

  const [listIsUpdating, setListIsUpdating] = React.useState<boolean>(false)

  const triggerListLoadAnimation = () => {
    setListIsUpdating(true)
    const timeout = window.scrollY === 0 ? 500 : 1000

    setTimeout(() => {
      setListIsUpdating(false)
    }, timeout)
  }

  React.useEffect(() => {
    triggerListLoadAnimation()

    const filtersToApply = Object.keys(activeFilters.filters).filter(
      (key) =>
        activeFilters.filters[key] &&
        ((!!activeFilters.filters[key].values &&
          activeFilters.filters[key].values!.length > 0) ||
          (activeFilters.filters[key].min && activeFilters.filters[key]?.max))
    )
    const sortedProducts = products[sortMethod]
    const filteredProducts =
      filtersToApply.length > 0
        ? sortedProducts.filter((product) => {
            const filteredForKey = {}

            filtersToApply.forEach((key) => {
              if (
                productSliderFilters.indexOf(key) !== -1 &&
                activeFilters.filters[key].min &&
                activeFilters.filters[key].max
              ) {
                // check if within min and max unless pristine
                if (!activeFilters.filters[key].pristine) {
                  const productValues = filterPropMap[key].props
                    ? filterPropMap[key].props.map((k) =>
                        parseFloat(product[k])
                      )
                    : [parseFloat(product[filterPropMap[key].prop])]

                  const isFilteredForKey = productValues.find(
                    (productValue) => {
                      return (
                        productValue &&
                        productValue >= activeFilters.filters[key].min! &&
                        productValue <= activeFilters.filters[key].max!
                      )
                    }
                  )

                  if (isFilteredForKey) {
                    filteredForKey[key] = true
                  }
                } else {
                  filteredForKey[key] = true
                }
              } else if (key === "Features") {
                // enforce all checked boolean fields
                filteredForKey["Features"] = true

                activeFilters.filters.Features.values!.forEach((booleanKey) => {
                  if (!product[filterPropMap[booleanKey].prop]) {
                    filteredForKey["Features"] = false
                  }
                })
              } else if (
                product[filterPropMap[key].prop] &&
                activeFilters.filters[key]?.values &&
                activeFilters.filters[key].values!.length > 0
              ) {
                // check if in filtered array
                const thisProp = product[filterPropMap[key].prop]

                if (typeof thisProp === "object") {
                  thisProp.forEach((propValue) => {
                    if (
                      activeFilters.filters[key].values!.indexOf(propValue) !==
                      -1
                    ) {
                      filteredForKey[key] = true
                    }
                  })
                } else if (
                  activeFilters.filters[key].values!.indexOf(thisProp) !== -1
                ) {
                  filteredForKey[key] = true
                }
              }
            })

            return (
              Object.values(filteredForKey).filter((v) => !!v).length ===
              filtersToApply.length
            )
          })
        : sortedProducts

    const productData = pageData?.products || []
    const filteredProductData = productData.filter(({ sku }) =>
      filteredProducts.find(({ model }) => model === sku)
    )
    const updatedAvailableFilters = getFilterValues(
      getFlattenedProducts(filteredProductData || [])
    )
    const mergedFilters = {
      ..._.omit(updatedAvailableFilters, productSliderFilters),
      ..._.pick(filters, productSliderFilters),
    }
    const sortedFilters = Object.keys(filterPropMap).reduce((acc, curr) => {
      if (mergedFilters[curr]) {
        acc[curr] = mergedFilters[curr]
      }
      return acc
    }, {})

    setRenderedProducts(_.take(filteredProducts, renderedProductCount))
    setFilteredProductCount(filteredProducts.length)
    setAvailableFilters(sortedFilters)
  }, [
    path,
    allProducts,
    products,
    sortMethod,
    activeFilters,
    renderedProductCount,
  ])

  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }, [activeFilters])

  const breadcrumbs = getBreadcrumbs(categories)
  let grandparentCategory
  let parentCategory
  let peerCategories
  let peerCategoryPath
  let childCategories
  let childCategoryPath
  let thisCategory

  if (categoryConfig && breadcrumbs) {
    switch (breadcrumbs.length) {
      case 1:
        // noNothing
        if (categoryConfig.subcategories?.length > 0) {
          childCategories = categoryConfig.subcategories.map(
            ({ category }) => category
          )
          childCategoryPath = breadcrumbs[0].slug
        } else {
          peerCategories = [categoryConfig.category]
          peerCategoryPath = "/collections"
        }
        break
      case 2:
        parentCategory = breadcrumbs[breadcrumbs?.length - 2]
        thisCategory = categoryConfig.subcategories.find(
          ({ category }) => category.name === breadcrumbs[1].name
        )
        if (
          thisCategory &&
          thisCategory.subcategories &&
          thisCategory.subcategories.length > 0
        ) {
          childCategories = thisCategory.subcategories.map(
            ({ category }) => category
          )
          childCategoryPath = breadcrumbs[1].slug
        } else if (categoryConfig.category.name === parentCategory.name) {
          peerCategories = categoryConfig.subcategories
          peerCategoryPath = breadcrumbs[0].slug
        }
        break
      case 3:
        parentCategory = categoryConfig.subcategories.find(({ category }) => {
          return category.name === breadcrumbs[1].name
        })
        thisCategory =
          parentCategory &&
          parentCategory.subcategories?.find(
            ({ category }) => category.name === breadcrumbs[2].name
          )

        if (
          thisCategory &&
          thisCategory.subcategories &&
          thisCategory.subcategories.length > 0
        ) {
          childCategories = thisCategory.subcategories
          childCategoryPath = breadcrumbs[2].slug
        } else if (parentCategory) {
          peerCategories = parentCategory.subcategories
          peerCategoryPath = breadcrumbs[1].slug
        }
        break
      case 4:
        grandparentCategory = categoryConfig.subcategories.find(
          ({ category }) => category.name === breadcrumbs[1].name
        )

        if (grandparentCategory) {
          parentCategory = grandparentCategory.subcategories.find(
            ({ category }) => category.name === breadcrumbs[2].name
          )

          if (parentCategory) {
            peerCategories = parentCategory.subcategories
            peerCategoryPath = breadcrumbs[2].slug
          }
        }
        break
    }
  }

  const [view, setView] = React.useState<ProductView>(ProductView.GRID)
  const [showFilters, setShowFilters] = React.useState<boolean>(true)
  const [showMobileFilters, setShowMobileFilters] =
    React.useState<boolean>(false)

  React.useEffect(() => {
    const hasFilters = Object.keys(filters).find((k) => {
      return filters[k] && filters[k].length > 1
    })
    if (!hasFilters) {
      setShowFilters(false)
      setShowMobileFilters(false)
    }
  }, [filters])

  const seoTitle =
    breadcrumbs?.length && useNewReleases
      ? "New Releases | SKB Cases"
      : breadcrumbs?.length
      ? `${formatDisplayedCategory(
          breadcrumbs[breadcrumbs.length - 1].name
        )} | SKB Cases`
      : "SKB Cases"

  return (
    <Layout>
      <Helmet title={seoTitle} />
      <Box
        p={bp(
          "30.5px calc((0.5 / 16) * 100vw) 0",
          null,
          "80px calc((0.5 / 16) * 100vw) 0"
        )}
      >
        <Box mb="12px">
          <Text
            fontFamily="Avenir"
            fontSize={bp("13.5px", null, "16px")}
            fontWeight="400"
            color={colors.midAccessible}
          >
            {breadcrumbs?.length &&
              breadcrumbs.map((breadcrumb, i) => (
                <React.Fragment key={i}>
                  <Link href={breadcrumb.slug} useAnchor>
                    {formatDisplayedCategory(breadcrumb.name)}
                  </Link>
                  {i !== breadcrumbs.length - 1 && " > "}
                </React.Fragment>
              ))}
          </Text>
        </Box>
      </Box>
      <Flex marginBottom="30px" justifyContent="space-between">
        <Box display="flex" pl="calc((0.5 / 16) * 100vw)">
          <Text
            alignSelf="center"
            fontFamily="Avenir"
            fontSize={bp("23px", null, "33px")}
            fontWeight="800"
            lineHeight={bp("30px", null, "40px")}
            color={colors.dark}
            overflowWrap="break-word"
          >
            {breadcrumbs?.length && useNewReleases
              ? `New Releases (${filteredProductCount})`
              : `${formatDisplayedCategory(
                  breadcrumbs[breadcrumbs.length - 1].name
                )} (${filteredProductCount})`}
          </Text>
        </Box>
        <Flex
          className="Filter__Wrapper"
          position="relative"
          alignItems="flex-end"
          p="0 calc((0.5 / 16) * 100vw)"
        >
          <LaptopUpOnly>
            <Button
              p="12px 20px"
              border="none"
              backgroundColor="transparent"
              variant="outline"
              _hover={{
                backgroundColor: bp(null, null, colors.light),
              }}
              _active={{
                backgroundColor: bp(colors.light, colors.light, null),
              }}
              onClick={() => {
                setShowFilters(!showFilters)
              }}
            >
              <Text
                mr="12px"
                fontFamily="Avenir"
                fontSize={bp("13.5px", null, "16px")}
                fontWeight="500"
                lineHeight={bp("13.5px", null, "22px")}
                color={colors.dark}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Text>

              <Filter />
            </Button>

            {view === ProductView.GRID ? (
              <Button
                p="12px 20px"
                border="none"
                backgroundColor="transparent"
                variant="outline"
                _hover={{
                  backgroundColor: colors.light,
                }}
                onClick={() => {
                  setView(ProductView.LIST)
                }}
              >
                <Text
                  mr="12px"
                  fontFamily="Avenir"
                  fontSize={bp("13.5px", null, "16px")}
                  fontWeight="500"
                  lineHeight={bp("13.5px", null, "22px")}
                  color={colors.dark}
                >
                  Show as List
                </Text>
                <List />
              </Button>
            ) : (
              <Button
                p="12px 20px"
                border="none"
                backgroundColor="transparent"
                variant="outline"
                _hover={{
                  backgroundColor: colors.light,
                }}
                onClick={() => {
                  setView(ProductView.GRID)
                }}
              >
                <Text
                  mr="12px"
                  fontFamily="Avenir"
                  fontSize={bp("13.5px", null, "16px")}
                  fontWeight="500"
                  lineHeight={bp("13.5px", null, "22px")}
                  color={colors.dark}
                >
                  Show as Grid
                </Text>
                <Grid />
              </Button>
            )}
          </LaptopUpOnly>

          <LaptopUpOnly>
            <Box>
              <SortDropdown value={sortMethod} onSort={onSort} />
            </Box>
          </LaptopUpOnly>

          <MobileOnly>
            <Button
              p="12px 20px"
              border="none"
              backgroundColor="transparent"
              variant="outline"
              _hover={{
                backgroundColor: colors.light,
              }}
              onClick={() => {
                setShowMobileFilters(true)
              }}
            >
              <Filter />
            </Button>
          </MobileOnly>
        </Flex>
      </Flex>
      <MobileOnly>
        <Box position="relative">
          <Box
            position="absolute"
            height="100%"
            width="20px"
            top={0}
            left={0}
            zIndex="1"
            background="linear-gradient(0.25turn,rgba(255,255,255,1), rgba(255,255,255,0))"
          />
          <Box
            position="absolute"
            height="100%"
            width="20px"
            background="linear-gradient(0.25turn,rgba(255,255,255,0), rgba(255,255,255,1))"
            top={0}
            right={0}
            zIndex="1"
          />
          <Flex position="relative" flexWrap="nowrap" overflow="auto">
            <Spacer
              w="calc((0.5 / 16) * 100vw)"
              minW="calc((0.5 / 16) * 100vw)"
            />
            {breadcrumbs &&
              childCategories &&
              childCategories.map((child) => {
                const { slug, name } = child
                return (
                  <Link
                    key={`${childCategoryPath}/${slug}`}
                    href={`${childCategoryPath}/${slug}`}
                    useAnchor
                    display="block"
                    opacity={0.5}
                    whiteSpace="nowrap"
                    margin="0px 30px 0px 0px"
                    p="4px 0"
                    fontFamily="Avenir"
                    fontSize="16px"
                    fontWeight="800"
                    lineHeight="20px"
                    color={colors.dark}
                  >
                    {formatDisplayedCategory(name)}
                  </Link>
                )
              })}
            {breadcrumbs &&
              !childCategories &&
              peerCategories &&
              peerCategories.map((peer) => {
                const category = peer.category ? peer.category : peer
                const { slug, name } = category

                return (
                  <Link
                    key={`${peerCategoryPath}/${slug}`}
                    href={`${peerCategoryPath}/${slug}`}
                    useAnchor
                    display="block"
                    opacity={
                      name === breadcrumbs[breadcrumbs.length - 1].name
                        ? 1
                        : 0.5
                    }
                    whiteSpace="nowrap"
                    margin="0px 30px 0px 0px"
                    p="4px 0"
                    fontFamily="Avenir"
                    fontSize="16px"
                    fontWeight="800"
                    lineHeight="20px"
                    textDecoration={
                      name === breadcrumbs[breadcrumbs.length - 1].name
                        ? "underline"
                        : "none"
                    }
                    color={colors.dark}
                  >
                    {formatDisplayedCategory(name)}
                  </Link>
                )
              })}
            <Spacer
              w="calc((0.5 / 16) * 100vw)"
              minW="calc((0.5 / 16) * 100vw)"
            />
          </Flex>
        </Box>
      </MobileOnly>
      <Flex
        flexDirection="row"
        width="100%"
        p={bp("32px 0 80px 0", "32px 0 80px 0", "32px 0 80px 0")}
      >
        <LaptopUpOnly>
          {showFilters && (
            <Box flexShrink={0} w="4col" pl="calc((0.5 / 16) * 100vw)" h="100%">
              <Box
                ref={filterPanelRef}
                position={["-webkit-sticky", "sticky"]}
                top={bp(null, null, "129px", "71px")}
                overflowY="auto"
                h={`calc(100vh)`}
              >
                <Box p="20px 0" borderTop="solid 1px rgba(0, 0, 0, 0.1)">
                  {breadcrumbs &&
                    childCategories &&
                    childCategories.map((child) => {
                      const { slug, name } = child
                      return (
                        <Link
                          key={`${childCategoryPath}/${slug}`}
                          href={`${childCategoryPath}/${slug}`}
                          useAnchor
                          display="block"
                          opacity={0.5}
                          p="4px 0"
                          fontFamily="Avenir"
                          fontSize="16px"
                          fontWeight="800"
                          lineHeight="20px"
                          color={colors.dark}
                        >
                          {formatDisplayedCategory(name)}
                        </Link>
                      )
                    })}
                  {breadcrumbs &&
                    !childCategories &&
                    peerCategories &&
                    peerCategories.map((peer) => {
                      const category = peer.category ? peer.category : peer
                      const { slug, name } = category

                      return (
                        <Link
                          key={`${peerCategoryPath}/${slug}`}
                          href={`${peerCategoryPath}/${slug}`}
                          useAnchor
                          display="block"
                          opacity={
                            name === breadcrumbs[breadcrumbs.length - 1].name
                              ? 1
                              : 0.5
                          }
                          p="4px 0"
                          fontFamily="Avenir"
                          fontSize="16px"
                          fontWeight="800"
                          lineHeight="20px"
                          textDecoration={
                            name === breadcrumbs[breadcrumbs.length - 1].name
                              ? "underline"
                              : "none"
                          }
                          color={colors.dark}
                        >
                          {formatDisplayedCategory(name)}
                        </Link>
                      )
                    })}
                </Box>
                <Filters
                  path={path || []}
                  lastClear={lastClear}
                  filters={filters}
                  availableFilters={availableFilters}
                  activeFilters={activeFilters}
                  onFilter={onFilter}
                  onClearFilter={onClearFilter}
                />
                <Spacer height={`${offset}px`} />
              </Box>
            </Box>
          )}
        </LaptopUpOnly>
        {view === ProductView.GRID ? (
          <Box
            position="relative"
            width="100%"
            p={"0 calc((0.5 / 16) * 100vw)"}
          >
            <Box
              position="absolute"
              top="0"
              left="0"
              transition={`opacity 200ms ${animations.bezier}`}
              w="100%"
              h="100%"
              opacity={listIsUpdating ? 0.5 : 0}
              backgroundColor={colors.white}
              pointerEvents="none"
            />
            <ProductGrid
              containerRef={productGridRef}
              products={renderedProducts}
            />
          </Box>
        ) : (
          <Box
            position="relative"
            width="100%"
            p={"0 calc((0.5 / 16) * 100vw)"}
          >
            <Box
              position="absolute"
              top="0"
              left="0"
              transition={`opacity 200ms ${animations.bezier}`}
              w="100%"
              h="100%"
              opacity={listIsUpdating ? 0.5 : 0}
              backgroundColor={colors.white}
              pointerEvents="none"
            />
            <ProductList
              mobileContainerRef={productListMobileRef}
              desktopContainerRef={productListDesktopRef}
              products={renderedProducts}
            />
          </Box>
        )}
        <FilterSlideout
          isOpen={showMobileFilters}
          view={view}
          sort={sortMethod}
          path={path || []}
          lastClear={lastClear}
          filters={filters}
          availableFilters={availableFilters}
          activeFilters={activeFilters}
          onFilter={onFilter}
          onClearFilter={onClearFilter}
          setView={setView}
          setSort={setSortMethod}
          onClose={() => {
            setShowMobileFilters(false)
          }}
        />
      </Flex>
    </Layout>
  )
}

export default Collections
